import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { PriceInfoView } from '@euler/app/flows/task/pages/quotation/components/quotation-form/PriceInfoView';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { Checkbox } from '@euler/components/form/Checkbox';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  QuotationConstructionItemStaged,
  QuotationMaterialItemStaged,
} from '@euler/model/task-detail';
import { withConfirmation } from '@euler/utils';
import produce from 'immer';
import { memo, useCallback, useMemo } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { MaterialItemView } from './MaterialItemView';

export const QuotationSubjectConstructionItemView = memo(
  ({
    editable,
    item,
    style,
    onEdit,
    onChange,
    onRemove,
    onAddMaterial,
    onEditMaterial,
  }: {
    editable?: boolean;
    item: QuotationConstructionItemStaged;
    onEdit?: (item: QuotationConstructionItemStaged) => void;
    onChange?: (item: QuotationConstructionItemStaged) => void;
    onRemove?: (item: QuotationConstructionItemStaged) => void;
    onAddMaterial?: (item: QuotationConstructionItemStaged) => void;
    onEditMaterial?: (
      item: QuotationConstructionItemStaged,
      index: number,
    ) => void;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();

    const materials = useMemo(
      () =>
        editable ? item.materials : item.materials.filter(x => x.included),
      [editable, item.materials],
    );

    const onCheckChange = useCallback(
      (checked: boolean) => {
        const updated = produce(item, draft => {
          draft.included = checked;
          if (checked) {
            if (draft.materials.every(x => !x.included)) {
              if (draft.materials.length === 1) {
                draft.materials[0].included = true;
              } else {
                draft.materials.forEach(material => {
                  material.included = material.defaultIncluded ?? false;
                });
              }
            }
          } else {
            draft.materials.forEach(material => {
              material.included = false;
            });
          }
        });
        onChange?.(updated);
      },
      [item, onChange],
    );

    const onMaterialChange = useCallback(
      (index: number, material: QuotationMaterialItemStaged) => {
        const updated = produce(item, draft => {
          if (draft.materials[index] != null) {
            draft.materials[index] = material;
            if (draft.materials.some(x => x.included) && !draft.included) {
              draft.included = true;
            }
            if (material.included && material.optionGroup) {
              const materialsInGroup = draft.materials.filter(
                (x, i) => x.optionGroup === material.optionGroup && i !== index,
              );
              materialsInGroup.forEach(x => (x.included = false));
            }
          }
        });
        onChange?.(updated);
      },
      [item, onChange],
    );

    const onEditPress = useCallback(async () => {
      onEdit?.(item);
    }, [item, onEdit]);

    const onRemovePress = useCallback(async () => {
      if (await withConfirmation({ title: '确定删除该施工项目？' })) {
        onRemove?.(item);
      }
    }, [item, onRemove]);

    const onAddMaterialPress = useCallback(() => {
      onAddMaterial?.(item);
    }, [item, onAddMaterial]);

    const onInternalEditMaterial = useCallback(
      (index: number, _material: QuotationMaterialItemStaged) => {
        onEditMaterial?.(item, index);
      },
      [item, onEditMaterial],
    );

    const onRemoveMaterial = useCallback(
      (index: number, _material: QuotationMaterialItemStaged) => {
        const updated = produce(item, draft => {
          if (draft.materials[index] != null) {
            draft.materials.splice(index, 1);
          }
        });
        onChange?.(updated);
      },
      [item, onChange],
    );

    return (
      <StackLayout style={style}>
        <StackLayout direction="row" gap={rpx(4)} alignItems="center">
          {editable && (
            <Checkbox
              shape="default"
              checked={item.included}
              size={rpx(20)}
              onChange={onCheckChange}
              css={`
                align-self: flex-start;
                top: ${rpx(4)}px;
              `}
            />
          )}
          <StackLayout flex={1} gap={rpx(4)}>
            <StackLayout direction="row" alignItems="center" gap={rpx(5)}>
              <Typography.Text flex={1} size={rpx(18)} medium>
                {item.name}
              </Typography.Text>
              {editable && (
                <Center.Touchable onPress={onEditPress}>
                  <Icon name="note" size={rpx(16)} color="#777" />
                </Center.Touchable>
              )}
              {editable && (
                <Center.Touchable onPress={onRemovePress}>
                  <Icon name="trash-o" size={rpx(16)} color="#777" />
                </Center.Touchable>
              )}
            </StackLayout>
            <HStack>
              <HStack
                justifyContent="flex-start"
                css={`
                  flex: 1;
                `}
              >
                <Typography.Text color="#777" numberOfLines={1} flexShrink={0}>
                  工时费:{' '}
                </Typography.Text>
                <PriceInfoView
                  price={item.price}
                  discountPrice={item.discountPrice}
                  discountRemark={item.discountRemark}
                />
              </HStack>
              {editable && (
                <LinkButton
                  text="添加配件"
                  textStyle={{ fontSize: rpx(12) }}
                  onPress={onAddMaterialPress}
                />
              )}
            </HStack>
            {item.remark ? (
              <StackLayout direction="row">
                <Typography.Text color="#777" flexShrink={0} numberOfLines={1}>
                  备注:{' '}
                </Typography.Text>
                <Typography.Text>{item.remark}</Typography.Text>
              </StackLayout>
            ) : null}
          </StackLayout>
        </StackLayout>
        <StackLayout
          gap={rpx(8)}
          css={`
            margin-top: ${rpx(8)}px;
          `}
        >
          {materials.map((material, index) => (
            <MaterialItemView
              editable={editable}
              index={index}
              material={material}
              key={index}
              onEdit={onInternalEditMaterial}
              onChange={onMaterialChange}
              onRemove={onRemoveMaterial}
            />
          ))}
        </StackLayout>
      </StackLayout>
    );
  },
);
